import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocalForageContext, useAuthContext } from '../context';
import { useApolloClient } from '@apollo/react-hooks';
import moment from 'moment';
import localforage from 'localforage';
import { useOfflineModeContext } from '../context/offlineContext';
import { useInsertEventLog } from './useInsertEventLog';
import { notificationManager } from '../utils/notificationManager';

import { getOrdersQuery } from '@msales/shared/graphql/queries/order/getOrders';
import { getSalesReportQuery } from '@msales/shared/graphql/queries/salesReport/getSalesReport';
import { getCustomerSalesHistoryQuery } from '@msales/shared/graphql/queries/report/getCustomerSalesHistory';
import { getDailyRealizationByGroupsQuery } from '@msales/shared/graphql/queries/realizationPlan/getDailyRealizationByGroups';
import { LAST_SUCCESSFUL_REPORTS_SYNC_KEY } from '../utils/reportsRefreshPolicy';

const LAST_SUCCESSFUL_DAILY_REPORT_SYNC_KEY =
  'msales_lastDailyRealizationSyncTime';

const reportConfigs = [
  {
    keyPrefix: 'orders',
    query: getOrdersQuery,
    getVariables: () => {
      const startDate = moment().startOf('month').format('YYYY-MM-DD');
      const endDate = moment().format('YYYY-MM-DD');
      return {
        startDate,
        endDate,
        filter: 0,
        limit: 10000,
      };
    },

    extractData: (data) => {
      const orderData = data?.orders;
      const nodes = orderData?.nodes;
      const totalCount = orderData?.totalCount ?? nodes?.length ?? 0;
      if (!nodes) throw new Error("Neočekivana struktura za 'ordersNodes'.");
      return {
        nodes,
        totalCount,
        documentSummary: orderData?.documentSummary || undefined,
      };
    },

    storageKeys: {
      nodes: 'Nodes',
      totalCount: 'TotalCount',
      documentSummary: 'DocumentSummary',
    },
  },
  {
    keyPrefix: 'salesReport',
    query: getSalesReportQuery,
    getVariables: () => {
      const startDate = moment().startOf('month').format('YYYY-MM-DD');
      const endDate = moment().format('YYYY-MM-DD');
      return { startDate, endDate };
    },
    extractData: (data) => {
      if (!data?.salesReport && !data?.correctedSalesReport) {
        throw new Error("Neočekivana struktura za 'salesReport'.");
      }

      return {
        result: {
          salesReport: data.salesReport,
          correctedSalesReport: data.correctedSalesReport,
        },
      };
    },
    storageKeys: {
      result: 'Result',
    },
  },
  {
    keyPrefix: 'dailyRealizationByGroups',
    query: getDailyRealizationByGroupsQuery,
    getVariables: () => ({
      date: moment().format('YYYY-MM-DD'),
    }),
    extractData: (data) => {
      if (!Array.isArray(data?.dailyRealizationByGroups)) {
        throw new Error(
          "Neočekivana struktura za 'dailyRealizationByGroups'.",
        );
      }

      return {
        result: data.dailyRealizationByGroups,
      };
    },
    storageKeys: {
      result: 'Result',
    },
  },
  {
    keyPrefix: 'customerSalesHistory',
    query: getCustomerSalesHistoryQuery,
    getVariables: () => {
      const startDate = moment().startOf('month').format('YYYY-MM-DD');
      const endDate = moment().format('YYYY-MM-DD');

      return {
        customerId: 'ALL',
        dateFrom: startDate,
        dateTo: endDate,
      };
    },
    extractData: (data) => {
      if (!data?.customerSalesHistory) {
        throw new Error("Neočekivana struktura za 'customerSalesHistory'.");
      }

      return {
        result: data.customerSalesHistory,
      };
    },
    storageKeys: {
      result: 'Result',
    },
  },
];

const SYNC_LOCK_KEY = 'ReportsSyncLock';
const LAST_SYNC_TIME_KEY = 'createdAt';
const LOCK_DURATION = 5 * 60 * 1000;

const reportsStore = localforage.createInstance({ name: 'Reports' });

export const useReportsOfflineData = () => {
  const {
    loadingReports: loading,
    setLoadingReports: setLoading,
    refetchReports: refetch,
    setRefetchReports: setRefetch,
  } = useLocalForageContext();
  const { authUser } = useAuthContext();
  const { networkState } = useOfflineModeContext();
  const isOnline = networkState?.online;
  const { insertEvent } = useInsertEventLog();
  const apolloClient = useApolloClient();

  const isMounted = useRef(true);
  const isSyncing = useRef(false);
  const [syncInProgress, setSyncInProgress] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const acquireSyncLock = useCallback(async (forceAcquire = false) => {
    const currentTime = Date.now();

    try {
      const existingLock = await reportsStore.getItem(SYNC_LOCK_KEY);

      if (existingLock && currentTime - existingLock < LOCK_DURATION) {
        if (forceAcquire) {
          await reportsStore.removeItem(SYNC_LOCK_KEY);
        } else {
          return false;
        }
      }

      await reportsStore.setItem(SYNC_LOCK_KEY, currentTime);
      return true;
    } catch (err) {
      console.error('Error acquiring sync lock:', err);
      return false;
    }
  }, []);

  const releaseSyncLock = useCallback(async () => {
    try {
      await reportsStore.removeItem(SYNC_LOCK_KEY);
    } catch (err) {
      console.error('Error releasing sync lock:', err);
    }
  }, []);

  const shouldSync = useCallback(
    async (forceRefetch, allCurrentVars) => {
      if (isSyncing.current || syncInProgress) {
        return false;
      }

      if (!authUser || (networkState && !networkState.online)) return false;
      if (loading) return false;
      if (forceRefetch) return true;

      try {
        for (const config of reportConfigs) {
          const createdAt = await reportsStore.getItem(
            `${config.keyPrefix}CreatedAt`,
          );
          if (!createdAt) {
            return true;
          }
        }
      } catch (err) {
        console.error('Error checking report sync conditions:', err);
      }

      return false;
    },
    [authUser, loading, networkState, syncInProgress],
  );

  const syncAllReports = useCallback(
    async (allDefaultVars, forceSync = false) => {
      if (!isMounted.current || isSyncing.current) return false;

      const lockAcquired = await acquireSyncLock(forceSync);
      if (!lockAcquired) return false;

      try {
        isSyncing.current = true;
        setSyncInProgress(true);
        setLoading(true);
        notificationManager.startSync('reports', 'Izvještaji');

        const syncPromises = reportConfigs.map(async (config) => {
          const keyPrefix = config.keyPrefix;
          const reportVars = allDefaultVars[keyPrefix];

          try {
            const { data, error } = await apolloClient.query({
              query: config.query,
              variables: reportVars,
              fetchPolicy: 'no-cache',
            });

            if (!isMounted.current)
              throw new Error('Component unmounted during fetch.');
            if (error) throw error;

            const extractedData = config.extractData(data);

            for (const dataKey in config.storageKeys) {
              if (extractedData[dataKey] === undefined) continue;
              const storageSuffix = config.storageKeys[dataKey];
              const storageKey = `${keyPrefix}${storageSuffix}`;
              await reportsStore.setItem(storageKey, extractedData[dataKey]);
            }

            await reportsStore.setItem(`${keyPrefix}Variables`, reportVars);
            const currentTime = moment().format('DD/MM/YYYY HH:mm:ss');
            await reportsStore.setItem(`${keyPrefix}CreatedAt`, currentTime);

            if (keyPrefix === 'dailyRealizationByGroups') {
              try {
                localStorage.setItem(
                  `${LAST_SUCCESSFUL_DAILY_REPORT_SYNC_KEY}:${authUser.id}`,
                  new Date().toISOString(),
                );
              } catch {
                // Report sync remains successful if browser storage is unavailable.
              }
            }

            return { status: 'fulfilled', keyPrefix };
          } catch (err) {
            console.error(`Error syncing ${keyPrefix}:`, err);
            return { status: 'rejected', keyPrefix, reason: err };
          }
        });

        const results = await Promise.all(syncPromises);

        const currentTime = moment().format('DD/MM/YYYY HH:mm:ss');
        await reportsStore.setItem(LAST_SYNC_TIME_KEY, currentTime);

        if (isMounted.current) {
          const errors = results
            .filter((result) => result.status === 'rejected')
            .map(
              (result) =>
                `Greška [${result.keyPrefix}]: ${
                  result.reason?.message || 'Nepoznata greška'
                }`,
            );

          if (errors.length === 0) {
            try {
              localStorage.setItem(
                `${LAST_SUCCESSFUL_REPORTS_SYNC_KEY}:${authUser.id}`,
                new Date().toISOString(),
              );
            } catch {
              // Report sync remains successful if browser storage is unavailable.
            }
            insertEvent({ eventCode: 'SYNC', remark: 'Izvještaji' });
            notificationManager.completeSync('reports', true);
          } else {
            notificationManager.completeSync('reports', false, `Greška prilikom sinhronizacije: ${errors.join('; ')}`);
          }
        }
        return true;
      } catch (err) {
        console.error('Unexpected error in syncAllReports:', err);
        notificationManager.completeSync('reports', false, 'Neobična greška prilikom sinhronizacije izvještaja');
        return false;
      } finally {
        await releaseSyncLock();

        if (isMounted.current) {
          setLoading(false);
          setRefetch(false);
        }
        isSyncing.current = false;
        setSyncInProgress(false);
      }
    },
    [
      acquireSyncLock,
      apolloClient,
      authUser?.id,
      insertEvent,
      releaseSyncLock,
      setLoading,
      setRefetch,
    ],
  );

  useEffect(() => {
    if (syncInProgress) return;

    const runSyncProcess = async () => {
      if (!refetch) await new Promise(r => setTimeout(r, 8000));
      const allCurrentVars = reportConfigs.reduce((acc, config) => {
        acc[config.keyPrefix] = config.getVariables();
        return acc;
      }, {});

      try {
        const isForceSync = refetch === true;
        const should = await shouldSync(refetch, allCurrentVars);

        if (should && isMounted.current) {
          await syncAllReports(allCurrentVars, isForceSync);
        } else if (refetch && isMounted.current) {
          setRefetch(false);
        }
      } catch (err) {
        console.error('Error in main report sync process:', err);
        if (isMounted.current) {
          setLoading(false);
          setRefetch(false);
        }
      }
    };

    runSyncProcess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refetch,
    authUser,
    isOnline,
    shouldSync,
    syncAllReports,
    setRefetch,
    setLoading,
    syncInProgress,
  ]);

  return null;
};
